import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import { showToast } from '../utils/Toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    server: ''
  });
  const [formDisabled, setFormDisabled] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);

  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    if (!email) {
      tempErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      tempErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!password) {
      tempErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        errorMessages.forEach(error => {
          if (error) showToast(error, 'error');
        });
      }
      return;
    }

    try {
      const result = await dispatch(login({ email, password })).unwrap();
      dispatch(clearError());
      showToast('Login successful!', 'success');
      
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setErrors({ ...errors, server: err });
      
      // Special handling for rate limit errors
      if (err && typeof err === 'string' && err.includes('Too many login attempts')) {
        showToast(err, 'error', 6000); // Show for longer duration
        // Optionally disable the form
        setFormDisabled(true);
        // Optionally start a timer to re-enable the form
        setTimeout(() => setFormDisabled(false), 60000); // 1 minute
      } else {
        showToast(err || 'Login failed', 'error');
      }
    }
  };

  return (
    <AuthLayout>
      <div className="w-full">
        <div className="mb-10 flex flex-col items-center">
          <div className="mb-8 p-4">
            <img 
              src="/logo/JRT-logo.webp" 
              alt="JRT Roofing" 
              className="h-28 w-auto object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight text-center">Welcome back</h2>
          <p className="text-slate-500 mt-3 text-base text-center">Please sign in to your account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-900 ml-1">Email Address</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com" 
              className={`w-full px-5 py-4 rounded-xl border-2 bg-white transition-all duration-200 ${
                errors.email 
                  ? 'border-red-300 focus:ring-red-200 focus:border-red-500 bg-red-50/30' 
                  : 'border-slate-200 focus:border-[#0B1120] focus:ring-0 hover:border-slate-300'
              } outline-none placeholder-slate-400 text-slate-900 text-sm font-medium`}
            />
            {errors.email && (
              <p className="text-sm text-red-500 ml-1 font-medium">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-1.5">
            <div className="flex items-center justify-between px-1">
              <label className="text-sm font-semibold text-slate-900">Password</label>
              <Link 
                to="/forgot-password" 
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-all"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password" 
                className={`w-full px-5 py-4 rounded-xl border-2 bg-white transition-all duration-200 ${
                  errors.password 
                    ? 'border-red-300 focus:ring-red-200 focus:border-red-500 bg-red-50/30' 
                    : 'border-slate-200 focus:border-[#0B1120] focus:ring-0 hover:border-slate-300'
                } outline-none placeholder-slate-400 text-slate-900 text-sm font-medium pr-12`}
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-lg hover:bg-slate-50"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  {showPassword ? (
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  ) : (
                    <>
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                      />
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                      />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500 ml-1 font-medium">{errors.password}</p>
            )}
          </div>

          {errors.server && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start space-x-3 animate-fade-in">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-red-600 font-medium">{typeof errors.server === 'string' ? errors.server : 'An error occurred'}</span>
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading || formDisabled}
            className={`w-full py-4 rounded-xl font-bold text-white shadow-lg shadow-blue-900/20 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0
              ${(loading || formDisabled) 
                ? 'bg-slate-400 cursor-not-allowed shadow-none' 
                : 'bg-[#0B1120] hover:bg-[#1e293b]'
              }
            `}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Signing in...</span>
              </div>
            ) : formDisabled ? (
              'Too many attempts' 
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login; 